// src/features/clients/pages/CardScanPage.tsx
// Escanear tarjeta (23-oct-2026, «sin tablet»): el cliente trae SU QR y el empleado lo lee con la
// cámara del celular. La visita se registra al instante con el mismo modal que «Registrar visita»
// (source='scan'). Pedido por Oswaldo (Snowtea León), Irvin (Marrieto) y Lidia (Fresca Raíz).
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Layout from '../../../components/layout/Layout';
import CardScanCard, { CARD_SCAN_EVENT } from '../../../components/qr/CardScanCard';
import ScanQrSheet from '../../../components/qr/ScanQrSheet';
import RegisterVisitModal from '../components/RegisterVisitModal';
import { cardScanApi, type CardContact, type CardScanResumen } from '../../../api/cardScanApi';
import { useAuth } from '../../../context/AuthContext';
import { BaseApiError } from '../../../api/baseClient';
import { Users, CalendarDays, CreditCard, Smartphone, QrCode, History, Loader2, Search } from 'lucide-react';

const CardScanPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isCashback = (currentUser?.loyaltyMode || 'visits') === 'cashback';
  const [resumen, setResumen] = useState<CardScanResumen | null>(null);
  const [scanning, setScanning] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [cliente, setCliente] = useState<CardContact | null>(null);
  const [ultimo, setUltimo] = useState<{ nombre: string; saldo: number } | null>(null);

  const cargar = useCallback(() => {
    cardScanApi.resumen().then(setResumen).catch(() => { /* adorno */ });
  }, []);
  useEffect(() => {
    cargar();
    window.addEventListener(CARD_SCAN_EVENT, cargar);
    return () => window.removeEventListener(CARD_SCAN_EVENT, cargar);
  }, [cargar]);

  // La cámara leyó un código → se busca al cliente y se abre el modal de visita ya con él.
  const onCode = async (code: string) => {
    setScanning(false);
    setBuscando(true);
    try {
      const r = await cardScanApi.byCode(code);
      setCliente(r.contact);
    } catch (e) {
      const msg = e instanceof BaseApiError && e.status === 404
        ? 'Esta tarjeta no es de un cliente de tu negocio.'
        : (e as Error).message || 'No se pudo leer la tarjeta.';
      toast.error(msg, { duration: 5000 });
    } finally {
      setBuscando(false);
    }
  };

  const nombreDe = (c: CardContact) => `${c.first_name}${c.last_name ? ` ${c.last_name}` : ''}`.trim();

  return (
    <Layout>
      <div className="w-full">
        <div className="mb-6"><CardScanCard variante="completa" onScan={() => setScanning(true)} /></div>

        {ultimo && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 text-green-700 dark:text-green-300 text-sm flex flex-wrap items-center justify-between gap-2">
            <span>Visita de <strong>{ultimo.nombre}</strong> registrada · saldo {isCashback ? `$${ultimo.saldo}` : `${ultimo.saldo} pts`}</span>
            <button type="button" onClick={() => setScanning(true)} className="font-semibold underline">Escanear otra</button>
          </div>
        )}

        {/* ── Números ── */}
        {resumen?.enabled && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
            {[
              [CalendarDays, 'Hoy', resumen.hoy, 'visitas escaneadas'],
              [History, '30 días', resumen.mes, `visitas · ${resumen.clientes_mes} clientes`],
              [CreditCard, 'Con tarjeta', resumen.con_tarjeta, `de ${resumen.total} clientes`],
              [Users, 'Sin tarjeta', Math.max(0, resumen.total - resumen.con_tarjeta), 'aún no la tienen'],
            ].map(([Icon, t, n, sub], i) => {
              const I = Icon as React.ElementType;
              return (
                <div key={i} className="bg-surface border border-line rounded-xl p-4">
                  <div className="flex items-center gap-2 text-xs text-content-3"><I className="h-4 w-4" /> {t as string}</div>
                  <p className="text-2xl font-bold text-content mt-1">{n as number}</p>
                  <p className="text-xs text-content-3">{sub as string}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Cómo le doy su tarjeta a un cliente ── */}
        <div className="bg-surface border border-line rounded-xl p-5 mb-6">
          <h2 className="text-lg font-semibold text-content mb-1">¿Cómo le doy su tarjeta a un cliente?</h2>
          <p className="text-sm text-content-3 mb-4">La tarjeta es una página con su QR. No descarga nada: la guarda en la pantalla de inicio de su celular.</p>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              [Smartphone, 'Él mismo, en el kiosco', 'Al identificarse con su número en la tablet o en el QR del mostrador, aparece «Mi tarjeta» y la guarda en su celular.'],
              [QrCode, 'Tú, desde su historial', 'Abre al cliente en Lista de clientes → botón «Tarjeta»: se la mandas por WhatsApp, copias el enlace o la imprimes.'],
              [Search, 'Si no la trae', 'Búscalo por nombre o teléfono como siempre y registra la visita desde su historial. La tarjeta es un atajo, no un requisito.'],
            ].map(([Icon, t, d], i) => {
              const I = Icon as React.ElementType;
              return (
                <div key={i} className="rounded-xl bg-surface-2 p-4">
                  <I className="h-5 w-5 text-accent mb-2" />
                  <p className="font-semibold text-content text-sm">{t as string}</p>
                  <p className="text-xs text-content-3 mt-1">{d as string}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            <RouterLink to="/dashboard/clients" className="font-medium text-accent hover:underline">Ir a Lista de clientes →</RouterLink>
            <RouterLink to="/dashboard/business?tab=visits" className="font-medium text-accent hover:underline">Mi Negocio → Cómo registro visitas →</RouterLink>
          </div>
        </div>

        <p className="text-xs text-content-3">
          Las visitas escaneadas quedan con origen «escáner» en el historial de cada cliente y en Reportes, con el nombre de quien las registró.
        </p>
      </div>

      {scanning && <ScanQrSheet onCode={onCode} onClose={() => setScanning(false)} hint="Pide a tu cliente que abra su tarjeta y apunta al QR" />}

      {buscando && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>
      )}

      {cliente && (
        <RegisterVisitModal
          source="scan"
          client={{ id: String(cliente.id), name: nombreDe(cliente), phone: cliente.phone, tags: cliente.tags }}
          onClose={() => setCliente(null)}
          onDone={({ balance }) => {
            setUltimo({ nombre: nombreDe(cliente), saldo: balance });
            cargar();
          }}
        />
      )}
    </Layout>
  );
};

export default CardScanPage;
